
import { Component, Watch, Provide, Inject, ProvideReactive } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { find, isEmpty, filter } from 'lodash'

import gmapsInit from '@/_services/gmap.service'
import MarkerClusterer from '@google/markerclustererplus'

import OutdoorOverlay from '@/_services/outdoor.overlay.service'

// Models
import FloorModel from '@/_models/FloorModel'
import UserModel from '@/_models/UserModel'

// Mixins
import TableSortMixin from '@/components/mixins/TableSortMixin.vue'
import IndoorOutdoorMixin from '@/components/mixins/IndoorOutdoorMixin.vue'

// Helpers
import { Filter } from '@/_utils/filter'
import { prettyPinConfig } from '@/_utils/pretty-pin'
import { alertUserChangeLocation } from '@/_utils/card-alert'
import { MapFixedDevice } from '@/_utils/map-fixed-device'
import { Constant } from '@/_services/constant'
import { getDateTime } from '@/_utils/datetime'
import { TimeOutPromise } from '@/_utils/utils'

// Components
import UserType from '@/components/shared/UserType.vue'
import SelectFloorBuilding from '@/components/shared/SelectFloorBuilding.vue'
import UserSearchListTable from '@/components/user/UserSearchListTable.vue'
import FloorBuildingFilterFrom from '@/components/common/indoor-outdoor/FloorBuildingFilterFrom.vue'
import OutdoorSettingButton from '@/components/common/indoor-outdoor/OutdoorSettingButton.vue'

@Component({
  components: {
    SelectFloorBuilding,
    UserType,
    UserSearchListTable,
    FloorBuildingFilterFrom,
    OutdoorSettingButton,
  },
  computed: {},
})
export default class OutdoorDisplayMixin extends mixins(TableSortMixin, IndoorOutdoorMixin) {
  selectedMode: string = 'outdoor'
  filterUtilTableSort: any = new Filter('filter_outdoor_table_sort')

  isSelectFloorActive: boolean = false
  selectFloorData: any = []

  lastMapcenter: any = null

  filterUtilInOutdoorTable: any = new Filter('filter_user_search_page_202011')

  @Inject('is-observer')
  isReadOnly!: () => boolean

  @ProvideReactive('outdoor-setting-floor-opacity')
  floorOpacity: number = 0

  get isLoadingUsers(): boolean {
    return this.$store.state.users.loading
  }

  get isMapBoundMode(): boolean {
    return this.$route.name === 'outdoor_type_bound'
  }

  get isViewAllFloor(): boolean {
    return !this.selectedFloor._id
  }

  get canDisplayMap(): boolean {
    return (
      !isEmpty(this.selectedFloor.building_name) || // For all floor in a building.
      this.selectedFloor._id ||
      this.isGPSMode(this.selectedUser) ||
      this.isIndoorMode(this.selectedUser) ||
      this.isMapBoundMode
    )
  }

  get isValidMapLocation(): boolean {
    if (!this.pinMarkers.length) {
      return false
    }

    return true
  }

  get floorImgId(): any {
    return floor => 'floorMapImg' + floor._id + this.keyComponent
  }

  get centerMap(): { lat: number; lng: number } {
    if (isEmpty(this.selectedFloor._id) && !this.isViewAllFloor) {
      return {
        lat: 35.652832,
        lng: 139.839478,
      }
    }

    if (this.isViewAllFloor) {
      return this.getBuildingLatLon(this.selectedFloor.building_name)
    }

    return {
      lat: (this.selectedFloor.floor_sw_lat + this.selectedFloor.floor_ne_lat) / 2,
      lng: (this.selectedFloor.floor_sw_lon + this.selectedFloor.floor_ne_lon) / 2,
    }
  }

  get filteredUsers(): UserModel[] {
    const targetField: string = this.pageFilters.selectedFilter
    if (!this.users) {
      return []
    }

    return this.users.filter((item: any): boolean => {
      const searchKey =
        String(item[targetField])
          .toLowerCase()
          .indexOf(this.pageFilters.searchQuery.toString().toLowerCase()) !== -1

      const validPeople = item.user_type === 'people' && this.isCheckedPeople
      const validThing = item.user_type === 'things' && this.isCheckedThings

      return searchKey && !this.isCardOff(item) && !this.hasAlert(item) && (validPeople || validThing)
    })
  }

  @ProvideReactive('outdoor-beacon-display')
  get beaconDisplay(): boolean {
    return this.mapControllerConfig.beacon_display
  }

  @ProvideReactive('outdoor-beacon-animation')
  get beaconAnimation(): boolean {
    return this.mapControllerConfig.beacon_animation
  }

  @ProvideReactive('outdoor-beacon-setting-loading')
  get beaconSettingLoading(): boolean {
    return this.isSysSettingLoading
  }

  @Provide('outdoor-change-beacon-display')
  updateBeaconDisplay() {
    this.updateSetting('outdoor', 'beacon_display')
  }

  @Provide('outdoor-update-beacon-animation')
  updateBeaconAnimation() {
    this.updateSetting('outdoor', 'beacon_animation')
  }

  @Provide('outdoor-update-floor-stranparent')
  updateFloorStranparent(val) {
    this.floorOpacity = val
  }

  handleChangeFloor(floor: FloorModel) {
    this.searchQueryFloor = floor.name || ''
    this.selectedFloor = floor

    if (!this.widgetOptions) {
      this.localSaveLastFloor(floor)
    }
    this.handleChangeBuilding(floor.building_name)
  }

  async checkAndChangeFloor() {
    const userId = this.$route.params.user_id
    const fixedId = this.$route.params.fixed_id
    let floorId = ''
    if (userId) {
      // Get user location from state if have
      let newUser: UserModel = new UserModel()
      if (this.users.length) {
        newUser = this.users.find(user => user._id === userId) || new UserModel()
      } else {
        await this.$store.dispatch('users/getOne', userId).then(user => {
          newUser = user
        })
      }

      this.checkedRows = [...this.checkedRows, newUser]
      if (newUser) {
        floorId = newUser?.current_floor_id
      }
    } else if (fixedId) {
      floorId = this.floorIdByFixedDeviceId(fixedId)
    }

    if (floorId) {
      const floor = this.floorById(floorId)
      return this.handleChangeFloor(floor)
    }

    if (this.isMapBoundMode || !this.validFloors.length) {
      // Display map without floor
      return await this.displayMap()
    }

    const lastFloor = this.getLastSelectedFloor()
    return this.handleChangeFloor(lastFloor)
  }

  @Watch('users')
  async usersChanged(newList, oldList) {
    alertUserChangeLocation(this, newList, oldList)
  }

  @Watch('selectedMode')
  selectedModeChange(mode) {
    this.$router.push({ name: mode }).catch(error => false)
  }

  @Watch('selectedFloor')
  async selectedFloorChanged() {
    this.searchQueryFloor = this.selectedFloor.name
    this.setDataSetting()
    this.displayMap()
  }

  @Watch('checkedRows')
  async onCheckedRowsChange(users) {
    this.checkAndSetPinSelectedStatus()
    if (users.length === 0) {
      this.fitBounds()
      this.fixZoom()
    }
  }

  @Watch('selectedPinMarkers')
  async onSelectedPinMarkersChange(markers) {
    if (!this.google || !markers?.length) {
      return
    }

    this.bounds = new this.google.maps.LatLngBounds()
    for (const marker of markers) {
      if (marker.getVisible()) {
        this.bounds.extend(marker.getPosition())
      }
    }

    this.fitBounds()
  }

  @Watch('floorOpacity')
  async changeOpacity() {
    this.setFloorOpacity()
  }

  @Watch('selectedBuildingName')
  async selectedBuildingNameChanged(newVal, oldVal) {
    if (oldVal !== '') {
      this.searchQueryFloor = ''
    }
  }

  isHiddenFixedDevice(beacon): boolean {
    const beaconBuilding = this.beaconBuilding(beacon)
    if (
      beaconBuilding &&
      beaconBuilding === this.selectedFloor.building_name &&
      beacon.floor_id !== this.selectedFloor._id &&
      !this.isViewAllFloor
    ) {
      return true
    }

    return false
  }

  fitBounds() {
    if (!this.map) {
      return
    }

    const bounds = this.map.getBounds()
    const ne = bounds.getNorthEast() // LatLng of the north-east corner
    const sw = bounds.getSouthWest() // LatLng of the south-west corder
    const invalidCoords = [0, 180]
    if (
      invalidCoords.indexOf(ne.lat()) < 0 &&
      invalidCoords.indexOf(ne.lng()) < 0 &&
      invalidCoords.indexOf(sw.lat()) < 0 &&
      invalidCoords.indexOf(sw.lng()) < 0
    ) {
      this.map?.fitBounds(this.bounds)
    }
  }

  fitBoundsFirstLoad() {
    for (const marker of this.pinMarkers) {
      if (marker.users?.length || marker.user_id) {
        this.bounds.extend(marker.getPosition())
      }
    }

    this.fitBounds()
  }

  setCenterMap(marker) {
    const center = marker.getPosition()
    this.lastMapcenter = center
    this.map.setCenter(center)
  }

  gotoUserRoute(userId: string): void {
    if (this.$route.params.user_id !== userId) {
      this.$router
        .push({
          name: 'outdoor_user',
          params: { user_id: userId },
        })
        .catch(error => false)
    }
  }

  setFloorOpacity(): void {
    const el = document.getElementById(this.floorImgId(this.selectedFloor))
    if (!el) {
      return
    }

    el.style.opacity = (this.floorOpacity / 100).toString()
  }

  async addFloorsToMap() {
    const el = document.getElementById(`mapOutdoor${this.keyComponent}`)
    if (el === null) {
      return
    }

    this.floorOpacity = this.selectedFloor.floor_opacity

    if (!this.map) {
      this.google = await gmapsInit()
      this.map = new this.google.maps.Map(el, {
        tilt: 0,
        clickableIcons: false,
        rotateControl: false,
        streetViewControl: false,
        gestureHandling: this.$isMobileSize() ? 'greedy' : 'cooperative',
        zoomControl: !this.$isMobileSize(),
        fullscreenControl: !this.$isMobileSize(),
      })

      // Save the scaled to local store when change zoom
      this.map.addListener('zoom_changed', this.saveTheScaledOfThisBuiding)

      OutdoorOverlay(this, this.google, this.map)
    }

    this.map.setCenter(this.centerMap)
    this.map.setZoom(this.mapZoom)
  }

  async displayMap() {
    try {
      this.removeAllMarker()

      await this.addFloorsToMap()
      if (this.isFirstLoad && !this.widgetOptions) {
        if (!this.users.length) {
          await this.$store.dispatch('users/getAll', { floor: 'all' })
        }

        this.refreshUsersInFloor()
      }

      this.bounds = new this.google.maps.LatLngBounds()
      await this.displayMakers()

      this.generateClusters()
      this.$nextTick(() => {
        this.isFirstLoad = false
      })
      ;(window as any).openCameraModal = this.openCameraModal
    } catch (error) {
      console.error(error)
    }
  }

  generateClusters() {
    const markerCluster = new MarkerClusterer(
      this.map,
      this.pinMarkers.filter(pin => !pin.fixed_id || (pin.fixed_id && pin.users && pin.users.length)),
      {
        averageCenter: true,
        imagePath: '/images/m',
        maxZoom: 18, // when zoom level is 18, all cluster will be Decluster
      }
    ) as any

    markerCluster.setCalculator((markers, numStyles) => {
      let index: number = 0
      let count: number = 0

      for (const marker of markers) {
        if (marker.users && marker.users.length) {
          count += marker.users.length
        }
      }

      let total: number = count
      while (total !== 0) {
        total = parseInt((total / 5).toString(), 10)
        index++
      }

      index = Math.min(index, numStyles)

      return {
        text: count,
        index,
      }
    })
  }

  localSaveLastFloor(floor): void {
    if (floor._id) {
      this.filterUtilLastFloor.save(floor._id)
    }
  }

  handleShowFloorSelectModal(buildingName: string): void {
    this.selectFloorData = {
      selectedFloor: this.selectedFloor,
      buildingName,
      floors: this.floors,
    }

    this.isSelectFloorActive = true
  }

  handleSelectFloorModalDone(floor: FloorModel): void {
    this.selectedFloor = floor
    this.isSelectFloorActive = false
    this.localSaveLastFloor(floor)
    this.handleChangeBuilding(floor.building_name)
  }

  removeInvalidMaker(): void {
    for (let index = this.pinMarkers.length - 1; index >= 0; --index) {
      const marker = this.pinMarkers[index]
      // Just remove pin user, ignore beacon pin
      if (marker.user_id) {
        const found = find(this.filteredUsers, user => {
          return this.isGPSMode(user) && user._id === marker.user_id
        })

        if (!found) {
          marker.setMap(null)
          this.pinMarkers.splice(index, 1)
        }
      }
    }
  }

  async displayMakers() {
    this.removeInvalidMaker()

    return await this.$nextTick(async () => {
      return await TimeOutPromise(() => {
        // Attach beacons to map
        for (const fixedDevice of this.filterFixedDevices) {
          const mapFixedDevice = new MapFixedDevice(this)
          mapFixedDevice.setFixedDevice(fixedDevice)
          mapFixedDevice.showMarkerFixedDevice()
        }

        // Attach users outdoor to map
        for (const user of this.filteredUsers) {
          this.createMarkerUser(user)
        }

        // If after draw map, no pin found. Then move to outdoor normal mode.
        if (!this.pinMarkers.length) {
          // Escape widget display
          if (!this.widgetOptions) {
            this.$router.push({ name: 'outdoor' }).catch(error => false)
          }
        } else if (this.lastMapcenter) {
          if (this.map && !this.map.getCenter()) {
            this.map.setCenter(this.lastMapcenter)
          }
        } else if (this.map && !this.map.getCenter()) {
          this.setCenterMap(this.pinMarkers[0])
        }

        if (this.isFirstLoad && this.isMapBoundMode) {
          this.fitBoundsFirstLoad()
          this.fixZoom()
        }
      }, 200)
    })
  }

  fixZoom(): void {
    if (this.map && this.map.getZoom() > Constant.MAP_MAX_INIT_ZOOM) {
      this.map.setZoom(Constant.MAP_MAX_INIT_ZOOM)
    }
  }

  pinIconUser(user: UserModel): string {
    return prettyPinConfig({
      type: 'gps',
      isSelected: this.isSelectedByUsersAndCheckedRow([user]),
      nearestUsers: [user],
      userType: user.user_type,
      uniqueId: user._id,
    })
  }

  beaconBuilding(beacon) {
    for (const floor of this.floors) {
      if (floor._id === beacon.floor_id) {
        return floor.building_name
      }
    }

    return null
  }

  createMarkerUser(user) {
    if (!this.isGPSMode(user)) {
      return
    }

    // Find existed pin user
    const foundMarker = this.findExistMarker(user)

    if (foundMarker) {
      let deltaLat
      let deltaLng
      let i = 0
      let oldLat = foundMarker.lat
      let oldLng = foundMarker.lon
      const acceptableDistance = this.setting.gps_error_acceptable_rank || 0

      if (oldLat !== user.lat || oldLng !== user.lon) {
        const fromCord = new this.google.maps.LatLng(oldLat, oldLng)
        const toCord = new this.google.maps.LatLng(user.lat, user.lon)
        const movedDistance = this.google.maps.geometry.spherical.computeDistanceBetween(fromCord, toCord)

        if (movedDistance <= acceptableDistance) {
          return
        }

        deltaLat = (user.lat - oldLat) / 10
        deltaLng = (user.lon - oldLng) / 10
        const moveMarker = () => {
          oldLat += deltaLat
          oldLng += deltaLng
          foundMarker.setPosition(new this.google.maps.LatLng(oldLat, oldLng))

          if (i !== 10) {
            i++
            oldLat += deltaLat
            oldLng += deltaLng
            setTimeout(moveMarker, 10)
          }
        }
        moveMarker()
        foundMarker.lat = user.lat
        foundMarker.lon = user.lon
      }

      return
    }

    const userLatLon = new this.google.maps.LatLng(user.lat, user.lon)
    this.bounds.extend(userLatLon)

    const marker = new this.google.maps.Marker({
      position: userLatLon,
      map: this.map,
      title: user.name,
      icon: this.pinIconUser(user),
    })

    if (this.checkedRows.find((userChecked: UserModel) => userChecked._id === user._id)) {
      this.setCenterMap(marker)
    }

    const cardInfo = user.san_id || user.major + '/' + user.minor
    marker.infowindow = new this.google.maps.InfoWindow({
      content: '[' + cardInfo + ']' + user.name,
    })

    this.google.maps.event.addListener(marker, 'click', () => {
      this.handlePinClick(marker)
    })

    marker.user_id = user._id
    marker.lat = user.lat
    marker.lon = user.lon
    marker.users = [user]
    this.pinMarkers.push(marker)

    this.google.maps.event.addListener(marker, 'mouseover', function (this: any) {
      this.infowindow.open(this.map, this)
    })

    this.google.maps.event.addListener(marker, 'mouseout', function (this: any) {
      this.infowindow.close()
    })
  }

  findExistMarker(user: UserModel) {
    const markersFiltered = filter(this.pinMarkers, mrk => {
      return mrk.user_id === user._id
    })

    if (markersFiltered.length <= 1) {
      return markersFiltered[0]
    }

    const sortedExist = markersFiltered.sort(
      (a, b) => getDateTime(b.measureded_at) - getDateTime(a.measureded_at)
    )

    for (let index = 1; index < sortedExist.length; index++) {
      const maker = sortedExist[index]
      const pinExist = find(this.pinMarkers, mrk => {
        return mrk.user_id === maker.user_id && mrk.lat === maker.lat && mrk.lon === maker.lon
      })

      if (pinExist) {
        pinExist.setMap(null)
        this.pinMarkers.splice(index, 1)
      }
    }

    return sortedExist[0]
  }

  getBuildingLatLon(buildingName): { lat: number; lng: number } {
    let numofFloor = 0
    let lat = 0
    let lng = 0
    if (!buildingName) {
      for (const floor of this.floors) {
        numofFloor++
        lat += (floor.floor_sw_lat + floor.floor_ne_lat) / 2
        lng += (floor.floor_sw_lon + floor.floor_ne_lon) / 2
      }
    } else {
      for (const floor of this.floors) {
        if (floor.building_name === buildingName) {
          numofFloor++
          lat += (floor.floor_sw_lat + floor.floor_ne_lat) / 2
          lng += (floor.floor_sw_lon + floor.floor_ne_lon) / 2
        }
      }
    }

    if (numofFloor > 0) {
      lat = lat / numofFloor
      lng = lng / numofFloor
    }

    return { lat, lng }
  }

  floorById(floorId: string): FloorModel {
    return this.validFloors.find(item => item._id === floorId) || new FloorModel()
  }

  floorIdByFixedDeviceId(fixedId: string): string {
    const fixedDevice = this.fixedDevices.find(item => item._id === fixedId)
    if (fixedDevice) {
      return fixedDevice.floor_id
    }

    return ''
  }

  loadMapWidgetOutDoor() {
    if (this.widgetOptions.floor === 'all') {
      const floorAll = new FloorModel()
      floorAll.name = this.$t('all') as string
      floorAll.building_name = this.floors[0].building_name

      return this.handleChangeFloor(floorAll)
    }

    const floorWidgetOutDoorSelect = this.floorById(this.widgetOptions.floor as string)
    this.handleChangeFloor(floorWidgetOutDoorSelect)
  }

  async loadMapOutDoor() {
    this.checkedRows = this.filterUtilCheckedRows.get([])
    this.pageFilters = this.filterUtilInOutdoorTable.get(this.pageFilters)

    await this.$store.dispatch('settings/getFromLocal')
    await this.$store.dispatch('cards/getAll', { card_type: 'all' })
    await this.$store.dispatch('beacons/getAll')

    this.$store.dispatch('floors/getAll').then(async () => {
      this.checkAndChangeFloor()
    })

    if (this.manageFeature && this.manageFeature.geofence) {
      await this.$store.dispatch('geofences/getAll')
      this.refreshUsersInGeofences()
    }
  }

  // After page created
  async created() {
    this.mapMode = 'outdoor'

    if (!!this.widgetOptions) {
      return this.loadMapWidgetOutDoor()
    }

    await this.loadMapOutDoor()
  }

  beforeDestroy() {
    // Remove all events and instance
    this.google.maps.event.clearInstanceListeners(this.map)
    this.map = null
    this._isDestroyed = true
    this.$store.dispatch('users/clearSetTimeOut')
    clearTimeout(this.refreshTimer)

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }

    this.$store.dispatch('resetAllModulesState', ['geofences'])
  }
}
